import React from 'react';
import { useState, useEffect } from "react";

/**
 * Utils
 */
import { getUsedLocalStorage } from '../utils/misc';

/**
 * Hooks
 */
import useToken from '../hooks/useToken';
import { useTranslation } from 'react-i18next';
import { useAppMenu } from '../contexts/NavBarContext';

/**
 * Components
 */
import Version from '../components/Version';
import UserInfoComponent from '../components/User/UserInfoComponent';
import LinearProgressWithLabel from '../components/LinearProgressWithLabel';

/**
 * MUI Components
 */
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SdStorageIcon from '@mui/icons-material/SdStorage';

const Profile = () => {
    const { t } = useTranslation();
    const { token } = useToken();
    const { updateTitle } = useAppMenu();

    const pageTitle = t('Profile');
    updateTitle(pageTitle);

    const [storage, setStorage] = useState({ quota: 0, usage: 0 });
    const localStorageLimit = 5 * 1024 * 1024;
    const usedStorage = getUsedLocalStorage();
    const progress = (usedStorage / localStorageLimit) * 100;

    useEffect(() => {
        if (navigator.storage && navigator.storage.estimate) {
            navigator.storage.estimate().then(estimate => {
                setStorage({
                    quota: estimate.quota / (1024 * 1024),
                    usage: estimate.usage / (1024 * 1024),
                });
            }).catch(error => console.error("Storage estimate failed:", error));
        }
    }, []);

    const availableStorage = storage.quota - storage.usage;

    return (
        <Box sx={{ p: 2, pb: 8 }}>
            <Stack spacing={2}>
                <UserInfoComponent token={token} />

                <Card variant="outlined">
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <SdStorageIcon color="action" />
                            <Typography variant="h6">{t('Device statistics')}</Typography>
                        </Box>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('Used local storage')}: {usedStorage.toFixed(2)} KB
                        </Typography>
                        <LinearProgressWithLabel value={progress} />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1 }}>
                            <StorageIcon color="action" fontSize="small" />
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('Used local storage')}
                            </Typography>
                        </Box>

                        <Stack spacing={0.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('Total Storage')}
                                </Typography>
                                <Typography variant="body2">
                                    {storage.quota.toFixed(2)} MB
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('Used Storage')}
                                </Typography>
                                <Typography variant="body2">
                                    {storage.usage.toFixed(2)} MB
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('Available Storage')}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                    {availableStorage.toFixed(2)} MB
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                    <Version fixed={false} />
                </Box>
            </Stack>
        </Box>
    );
};

export default Profile;
